// Settings › Call reasons (BL-003): the company's reasons a call didn't book, in the order the call
// card offers them. Each says whether such a call is a lead (it counts toward the booking rate) and
// whether it's excused (it doesn't count against whoever took it). Retire one that calls have used;
// it's never deleted. Names are the company's own ("Called after 3:30"). Riverstone's; Blueprint's Calls
// is CallsPage.tsx.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { reasonMeans, type CallReason } from '../calls/reasons-model.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import '../calls/booking-rate.css';

type Draft = {
  id?: string;
  name: string;
  isLead: boolean;
  excused: boolean;
  active: boolean;
  rowVersion?: number;
};
const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Could not save. Try again.');

export function CallReasonsTab({ canEdit }: { canEdit: boolean }) {
  const [reasons, setReasons] = useState<CallReason[] | null>(null);
  const [again, setAgain] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    void client
      .call<{ reasons: CallReason[] }>('callReasons.list', { includeRetired: true })
      .then((r) => setReasons(r.reasons))
      .catch((e: unknown) => setError(say(e)));
  }, [again]);
  const save = async (d: Draft, sort?: number) => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<CallReason>('callReasons.save', {
        ...(d.id ? { id: d.id, rowVersion: d.rowVersion } : {}),
        name: d.name.trim(),
        isLead: d.isLead,
        excused: d.excused,
        active: d.active,
        ...(sort !== undefined ? { sort } : {}),
      });
      setDraft(null);
      setSaid(`${r.name} saved.`);
      setAgain((n) => n + 1);
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(false);
    }
  };
  // move one up or down: it takes its neighbour's place (the neighbour moves on its next save)
  const move = async (i: number, dir: -1 | 1) => {
    const list = reasons!;
    const a = list[i]!;
    const b = list[i + dir];
    if (!b) return;
    await save({ ...a }, b.sort + dir);
  };
  return (
    <section className="stone card call-reasons" aria-label="Call reasons">
      <div className="row between wrap">
        <h2 className="h3">Call reasons</h2>
        {canEdit && !draft && (
          <button
            type="button"
            className="btn"
            onClick={() => setDraft({ name: '', isLead: true, excused: false, active: true })}
          >
            Add a reason
          </button>
        )}
      </div>
      <p className="muted">
        Why a call didn’t book. A lead counts toward the booking rate; an excused reason doesn’t count against
        whoever took the call. Calls under a minute are excused whatever their reason.
      </p>
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {draft && (
        <ReasonForm
          draft={draft}
          busy={busy}
          onChange={setDraft}
          onSave={() => void save(draft)}
          onCancel={() => setDraft(null)}
        />
      )}
      {!reasons && !error && <p className="muted">Getting the reasons…</p>}
      {reasons && (
        <ul className="reason-list">
          {reasons.map((r, i) => (
            <li key={r.id} className={r.active ? '' : 'retired'}>
              <div>
                <b>{r.name}</b>
                {!r.active && <span className="chip say">Retired</span>}
                <span className="muted small block">{reasonMeans(r)}</span>
              </div>
              {canEdit && (
                <div className="row">
                  <button
                    type="button"
                    className="btn quiet"
                    aria-label={`Move ${r.name} up`}
                    disabled={busy || i === 0}
                    onClick={() => void move(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn quiet"
                    aria-label={`Move ${r.name} down`}
                    disabled={busy || i === reasons.length - 1}
                    onClick={() => void move(i, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn soft"
                    aria-label={`Change ${r.name}`}
                    onClick={() => setDraft({ ...r })}
                  >
                    Change
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReasonForm({
  draft,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: Draft;
  busy: boolean;
  onChange: (d: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <form
      className="fgrid reason-form"
      aria-label={draft.id ? `Change ${draft.name}` : 'A new call reason'}
      onSubmit={(e) => {
        e.preventDefault();
        if (draft.name.trim() && !busy) onSave();
      }}
    >
      <label className="wide">
        <span className="flabel">Name</span>
        <input
          className="field"
          value={draft.name}
          maxLength={120}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
        />
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={draft.isLead}
          onChange={(e) => onChange({ ...draft, isLead: e.target.checked })}
        />
        A lead: it counts toward the booking rate
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={draft.excused}
          onChange={(e) => onChange({ ...draft, excused: e.target.checked })}
        />
        Excused: it doesn’t count against whoever took the call
      </label>
      {draft.id && (
        <label className="check">
          <input
            type="checkbox"
            checked={!draft.active}
            onChange={(e) => onChange({ ...draft, active: !e.target.checked })}
          />
          Retired: no longer offered on a call
        </label>
      )}
      <div className="row">
        <SaveButton
          className="btn primary"
          busy={busy}
          busyLabel="Saving…"
          disabled={!draft.name.trim()}
          onClick={onSave}
        >
          Save
        </SaveButton>
        <button type="button" className="btn quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
